import React, { useState } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from 'contexts/ThemeContext';
import { getThemeList } from 'config/themes';
import { ReactComponent as PaletteIcon } from 'feather-icons/dist/icons/droplet.svg';
import { ReactComponent as CheckIcon } from 'feather-icons/dist/icons/check.svg';

// Fixed positioning container
const Container = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 9999;

  @media (max-width: 768px) {
    bottom: 1.5rem;
    right: 1.5rem;
  }
`;

// Main Floating Action Button (FAB)
const FAB = styled(motion.button)`
  ${tw`w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 cursor-pointer focus:outline-none`}
  background: linear-gradient(135deg, rgb(var(--color-primary-500)), rgb(var(--color-primary-600)));

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: scale(0.95);
  }

  svg {
    ${tw`w-6 h-6 text-white`}
  }

  @media (max-width: 768px) {
    ${tw`w-12 h-12`}

    svg {
      ${tw`w-5 h-5`}
    }
  }
`;

// Grid menu container
const ThemeGrid = styled(motion.div)`
  position: absolute;
  bottom: 5rem;
  right: 0;
  ${tw`rounded-2xl shadow-2xl p-4`}
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;

  @media (max-width: 768px) {
    bottom: 4.5rem;
    ${tw`p-3`}
    gap: 0.5rem;
    grid-template-columns: repeat(3, 1fr);
  }
`;

// Individual theme option
const ThemeOption = styled(motion.button)`
  ${tw`w-12 h-12 rounded-full shadow-xl flex items-center justify-center cursor-pointer focus:outline-none relative`}
  background-color: ${props => props.$color};
  border: 3px solid ${props => props.$isActive ? 'white' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.15);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.4);
  }

  &:active {
    transform: scale(0.9);
  }

  @media (max-width: 768px) {
    ${tw`w-10 h-10`}
  }
`;

// Check icon for active theme
const CheckIconContainer = styled.div`
  ${tw`absolute inset-0 flex items-center justify-center`}

  svg {
    ${tw`w-6 h-6 text-white`}
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.5));
  }

  @media (max-width: 768px) {
    svg {
      ${tw`w-5 h-5`}
    }
  }
`;

// Tooltip for theme name
const Tooltip = styled(motion.div)`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 0.5rem;
  ${tw`px-3 py-1 rounded-lg text-sm font-medium text-white whitespace-nowrap`}
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(4px);
  pointer-events: none;

  /* Tooltip arrow */
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(0, 0, 0, 0.9);
  }

  @media (max-width: 768px) {
    ${tw`text-xs px-2 py-1`}
    margin-bottom: 0.375rem;

    &::after {
      border-width: 4px;
    }
  }
`;

const FloatingThemeSwitcher = () => {
  const { currentTheme, changeTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredTheme, setHoveredTheme] = useState(null);
  const themes = getThemeList();

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setIsOpen(false);
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  // Animation variants for FAB rotation
  const fabVariants = {
    closed: { rotate: 0 },
    open: { rotate: 180 }
  };

  // Animation variants for grid container
  const gridVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      y: 20
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25,
        staggerChildren: 0.03,
        delayChildren: 0.05
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 20,
      transition: {
        duration: 0.2
      }
    }
  };

  // Animation variants for individual theme items
  const itemVariants = {
    hidden: {
      opacity: 0,
      scale: 0
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25
      }
    }
  };

  // Tooltip animation
  const tooltipVariants = {
    hidden: { opacity: 0, y: 5, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.2, ease: "easeOut" }
    }
  };

  return (
    <Container>
      {/* Theme Grid */}
      <AnimatePresence>
        {isOpen && (
          <ThemeGrid
            variants={gridVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {themes.map((theme, index) => (
              <div
                key={theme.id}
                style={{ position: 'relative' }}
                onMouseEnter={() => setHoveredTheme(theme.id)}
                onMouseLeave={() => setHoveredTheme(null)}
              >
                <ThemeOption
                  $color={theme.primaryColor}
                  $isActive={currentTheme === theme.id}
                  onClick={() => handleThemeChange(theme.id)}
                  variants={itemVariants}
                  aria-label={`Switch to ${theme.name} theme`}
                >
                  {currentTheme === theme.id && (
                    <CheckIconContainer>
                      <CheckIcon />
                    </CheckIconContainer>
                  )}
                </ThemeOption>

                {/* Tooltip on hover */}
                <AnimatePresence>
                  {hoveredTheme === theme.id && (
                    <Tooltip
                      variants={tooltipVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      {theme.name}
                    </Tooltip>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </ThemeGrid>
        )}
      </AnimatePresence>

      {/* Main FAB */}
      <FAB
        onClick={toggleMenu}
        variants={fabVariants}
        animate={isOpen ? "open" : "closed"}
        whileTap={{ scale: 0.9 }}
        aria-label="Theme switcher"
        aria-expanded={isOpen}
      >
        <PaletteIcon />
      </FAB>
    </Container>
  );
};

export default FloatingThemeSwitcher;
